// Everything the Settings drawer edits: the notification switches, the
// preference toggles and the request card size — plus their saves. Split out
// of pages/ProfilePage.tsx; the page's profile fetch seeds these through the
// setters returned here.
import React, { useState } from "react";
import { broadcastAcceptingStatus } from "../lib/acceptingStatus";
import { broadcastLiveControlVisible } from "../lib/liveControlVisibility";
import type { ProfileAutosave } from "./useProfileAutosave";

type Args = {
  autosave: ProfileAutosave;
  isAccepting: boolean;
  setIsAccepting: React.Dispatch<React.SetStateAction<boolean>>;
};

export function useProfileSettings({ autosave, isAccepting, setIsAccepting }: Args) {
  const { putProfileField } = autosave;
  const [isWebPushEnabled, setIsWebPushEnabled] = useState(true);

  // Settings toggles (default ON to preserve existing guest-page behavior)
  const [showFastPass, setShowFastPass] = useState(true);
  // askForNickname toggle removed (identity is account-only) — the
  // server field lives on untouched, nothing here reads it anymore.
  const [allowPersonalizedMessages, setAllowPersonalizedMessages] = useState(true);
  // Opt-in visibility of the Live/Offline control (default OFF = hidden = the
  // talent is treated as always Live). See lib/liveControlVisibility.
  const [showLiveToggle, setShowLiveToggle] = useState(false);
  // Cash-out receipt emails (Notifications → Emails). Default ON; these gate
  // only the two routine receipts — the safety emails always send.
  const [emailPayoutStarted, setEmailPayoutStarted] = useState(true);
  const [emailDepositLanded, setEmailDepositLanded] = useState(true);
  // Requests-tab card size (Preferences → Display). Missing/null on accounts
  // from before the feature → "compact" (the original size).
  const [requestCardSize, setRequestCardSize] = useState<"large" | "standard" | "compact">("compact");

  // Whether THIS device currently has an active web push subscription.
  // Drives the visual ON/OFF of the Desktop notifications status toggle.
  const [desktopActive, setDesktopActive] = useState(false);

  // Preferences: show/hide the Live/Offline control. Broadcasts immediately so
  // the always-mounted Header pill appears/disappears without a refetch.
  // Safety: turning the control OFF while Offline would strand the talent
  // invisible — the server flips them back to Live in the same write (see
  // routes/users.js), so we optimistically reflect that here too.
  const toggleLiveControl = async () => {
    const next = !showLiveToggle;
    setShowLiveToggle(next);
    broadcastLiveControlVisible(next);
    const ok = await putProfileField("liveToggle", { showLiveToggle: String(next) });
    if (!ok) {
      setShowLiveToggle(!next);
      broadcastLiveControlVisible(!next);
      return;
    }
    if (!next && !isAccepting) {
      setIsAccepting(true);
      broadcastAcceptingStatus(true);
    }
  };

  // Display size: optimistic pick, revert on save failure (same contract as
  // toggleField, just three-state).
  const pickCardSize = async (v: "large" | "standard" | "compact") => {
    if (v === requestCardSize) return;
    const prev = requestCardSize;
    setRequestCardSize(v);
    const ok = await putProfileField("requestCardSize", { requestCardSize: v });
    if (!ok) setRequestCardSize(prev);
  };

  return {
    isWebPushEnabled,
    setIsWebPushEnabled,
    showFastPass,
    setShowFastPass,
    allowPersonalizedMessages,
    setAllowPersonalizedMessages,
    showLiveToggle,
    setShowLiveToggle,
    emailPayoutStarted,
    setEmailPayoutStarted,
    emailDepositLanded,
    setEmailDepositLanded,
    requestCardSize,
    setRequestCardSize,
    desktopActive,
    setDesktopActive,
    toggleLiveControl,
    pickCardSize,
  };
}

export type ProfileSettings = ReturnType<typeof useProfileSettings>;
